import React, { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, Mail, Phone, Building2, FolderOpen, ChevronRight, Trash2, ToggleLeft, ToggleRight, UserX, UserCheck } from 'lucide-react';
import { DataTable, type ColumnDef } from '../../components/ui/DataTable';
import { Avatar } from '../../components/ui/Avatar';
import { PageHeader } from '../../components/ui/PageHeader';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import type { ExternalPhotographer, PaginatedResponse, ProjectStatus, ExternalPaymentStatus, BulkResult } from '../../api/types';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Drawer } from '../../components/ui/Drawer';
import { Modal, ConfirmDialog } from '../../components/ui/Modal';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonRow } from '../../components/ui/Skeleton';
import { BulkActionBar } from '../../components/ui/BulkActionBar';
import { RowActionsMenu } from '../../components/ui/RowActionsMenu';
import { useTableSelection } from '../../hooks/useTableSelection';
import { formatDate, formatCurrency } from '../../utils/format';
import { InfoSection, InfoRow } from '../../components/ui/InfoCard';
import { PriceOverrideSection } from '../../components/performances/PriceOverrideSection';
import { ValidityBadge } from '../../components/external-photographers/ValidityBadge';
import { MarkdownEditor } from '../../components/external-photographers/MarkdownEditor';
import ReactMarkdown from 'react-markdown';

import { useDebounce } from '../../hooks/useDebounce';

type ExtPhotoSortField = 'name' | 'city' | 'projects' | 'status';
type SortOrder = 'asc' | 'desc';

function sortPhotographers(list: import('../../api/types').ExternalPhotographer[], field: ExtPhotoSortField, order: SortOrder) {
  return [...list].sort((a, b) => {
    let cmp = 0;
    switch (field) {
      case 'name':     cmp = `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`); break;
      case 'city':     cmp = (a.city ?? '').localeCompare(b.city ?? ''); break;
      case 'projects': cmp = (a._count?.projects ?? 0) - (b._count?.projects ?? 0); break;
      case 'status':   cmp = Number(a.active) - Number(b.active); break;
    }
    return order === 'asc' ? cmp : -cmp;
  });
}

const STATUS_LABELS: Record<ProjectStatus, string> = {
  BOZZA: 'Bozza',
  PREVENTIVATO: 'Preventivato',
  CONFERMATO: 'Confermato',
  IN_CORSO: 'In corso',
  IN_ATTESA: 'In attesa',
  COMPLETATO: 'Completato',
  FATTURATO: 'Fatturato',
  CHIUSO: 'Chiuso',
  ANNULLATO: 'Annullato',
};

const PH_STATUS_COLORS: Record<ProjectStatus, string> = {
  BOZZA: '#6B7280',
  PREVENTIVATO: '#6366f1',
  CONFERMATO: '#0ea5e9',
  IN_CORSO: '#f59e0b',
  IN_ATTESA: '#8b5cf6',
  COMPLETATO: '#10b981',
  FATTURATO: '#059669',
  CHIUSO: '#9CA3AF',
  ANNULLATO: '#EF4444',
};

const PAYMENT_STATUS_LABELS: Record<ExternalPaymentStatus, string> = {
  DA_INCASSARE: 'Da incassare',
  PARZIALE: 'Parziale',
  INCASSATO: 'Incassato',
};

const PAYMENT_STATUS_COLORS: Record<ExternalPaymentStatus, { bg: string; color: string }> = {
  DA_INCASSARE: { bg: '#EF4444', color: '#FFFFFF' },
  PARZIALE: { bg: '#F59E0B', color: '#FFFFFF' },
  INCASSATO: { bg: '#22C55E', color: '#FFFFFF' },
};

function StatusBadgeSmall({ status }: { status: ProjectStatus }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '4px 8px',
      borderRadius: 'var(--radius-interactive)',
      fontSize: 11, fontWeight: 600,
      background: PH_STATUS_COLORS[status],
      color: '#FFFFFF',
    }}>
      {STATUS_LABELS[status]}
    </span>
  );
}

function PaymentBadgeSmall({ status }: { status: ExternalPaymentStatus }) {
  const c = PAYMENT_STATUS_COLORS[status];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '4px 8px',
      borderRadius: 'var(--radius-interactive)',
      fontSize: 11, fontWeight: 600,
      background: c.bg, color: c.color,
    }}>
      {PAYMENT_STATUS_LABELS[status]}
    </span>
  );
}

type FormData = {
  firstName: string; lastName: string; email: string; studioName: string;
  phone: string; city: string; address: string; vatNumber: string; fiscalCode: string;
  notes: string; active: boolean; validUntil: string;
};

const emptyForm = (): FormData => ({
  firstName: '', lastName: '', email: '', studioName: '',
  phone: '', city: '', address: '', vatNumber: '', fiscalCode: '', notes: '', active: true, validUntil: '',
});

export default function ExternalPhotographersPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);
  const [showInactive, setShowInactive] = useState(false);
  const [page, setPage] = useState(1);

  const [selectedPhotographer, setSelectedPhotographer] = useState<ExternalPhotographer | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState<FormData>(emptyForm());
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [bulkDeactivateOpen, setBulkDeactivateOpen] = useState(false);
  const [bulkReactivateOpen, setBulkReactivateOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const [sortBy, setSortBy] = useState<ExtPhotoSortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  const resetKey = `${debouncedSearch}|${showInactive}|${page}`;
  const sel = useTableSelection(resetKey);

  const handleSort = (field: ExtPhotoSortField) => {
    if (sortBy === field) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortOrder('asc'); }
  };

  const { data, isLoading } = useQuery<PaginatedResponse<ExternalPhotographer>>({
    queryKey: ['external-photographers', debouncedSearch, showInactive, page],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (!showInactive) params.set('active', 'true');
      const query = params.toString();
      return await apiGet<PaginatedResponse<ExternalPhotographer>>(
        `/external-photographers${query ? '?' + query : ''}`
      );
    },
  });

  const rawPhotographers = data?.data ?? [];
  const photographers = useMemo(() => sortPhotographers(rawPhotographers, sortBy, sortOrder), [rawPhotographers, sortBy, sortOrder]);


  const invalidate = () => qc.invalidateQueries({ queryKey: ['external-photographers'] });

  const createMutation = useMutation({
    mutationFn: (body: Partial<FormData>) => apiPost<ExternalPhotographer>('/external-photographers', body),
    onSuccess: () => {
      invalidate(); setShowNewModal(false); setForm(emptyForm());
      toast.success('Esterno creato');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...body }: Partial<FormData> & { id: string }) =>
      apiPatch<ExternalPhotographer>(`/external-photographers/${id}`, body),
    onSuccess: async (updated) => {
      invalidate();
      const detail = await apiGet<ExternalPhotographer>(`/external-photographers/${updated.id}`);
      setSelectedPhotographer(detail);
      setEditMode(false);
      toast.success('Esterno aggiornato');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      apiPatch<ExternalPhotographer>(`/external-photographers/${id}`, { active }),
    onSuccess: (updated) => {
      invalidate();
      if (selectedPhotographer?.id === updated.id) {
        setSelectedPhotographer(prev => prev ? { ...prev, active: updated.active } : null);
      }
      toast.success(updated.active ? 'Esterno riattivato' : 'Esterno disattivato');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/external-photographers/${id}`),
    onSuccess: (_, id) => {
      invalidate();
      if (selectedPhotographer?.id === id) setSelectedPhotographer(null);
      setDeleteTargetId(null);
      sel.clearSelection();
      toast.success('Esterno eliminato');
    },
    onError: (e: Error) => { toast.error(e.message); setDeleteTargetId(null); },
  });

  const bulkDeactivateMutation = useMutation({
    mutationFn: (ids: string[]) => apiPost<BulkResult>('/external-photographers/bulk/deactivate', { ids }),
    onSuccess: (res) => {
      invalidate(); sel.clearSelection(); setBulkDeactivateOpen(false);
      toast.success(`${res.updated} esterni disattivati`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const bulkReactivateMutation = useMutation({
    mutationFn: (ids: string[]) => apiPost<BulkResult>('/external-photographers/bulk/reactivate', { ids }),
    onSuccess: (res) => {
      invalidate(); sel.clearSelection(); setBulkReactivateOpen(false);
      toast.success(`${res.updated} esterni riattivati`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => apiPost<BulkResult>('/external-photographers/bulk/delete', { ids }),
    onSuccess: (res) => {
      invalidate(); sel.clearSelection(); setBulkDeleteOpen(false);
      toast.success(`${res.updated} esterni eliminati`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const openDrawer = useCallback(async (photographer: ExternalPhotographer) => {
    const detail = await apiGet<ExternalPhotographer>(`/external-photographers/${photographer.id}`);
    setSelectedPhotographer(detail);
    setEditMode(false);
  }, []);

  const handleCreate = (e: React.FormEvent) => { e.preventDefault(); createMutation.mutate(form); };
  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPhotographer) return;
    updateMutation.mutate({ id: selectedPhotographer.id, ...form });
  };

  interface PhotographerStats {
    totalPerformances: number;
    totalAgreedPrice: number;
    totalPayments: number;
    byStatus: Record<string, number>;
    byService: Array<{ serviceId: string; serviceName: string; count: number; totalAgreed: number }>;
    recentPerformances: Array<{ id: string; title: string; eventDate: string; agreedPrice: number; status: string; services: string[] }>;
  }

  const { data: stats, isLoading: statsLoading } = useQuery<PhotographerStats>({
    queryKey: ['external-photographer-stats', selectedPhotographer?.id],
    queryFn: () => apiGet<PhotographerStats>(`/external-photographers/${selectedPhotographer!.id}/stats`),
    enabled: !!selectedPhotographer?.id,
  });

  const totalBilled = selectedPhotographer?.projects?.reduce(
    (sum, pr) => sum + (pr.externalSalePrice ?? 0), 0
  ) ?? 0;
  const activeJobs = selectedPhotographer?.projects?.filter(
    pr => pr.status === 'IN_CORSO' || pr.status === 'CONFERMATO'
  ).length ?? 0;

  const selectedIds = [...sel.selectedIds];

  const photoColumns: ColumnDef<ExternalPhotographer>[] = [
    {
      key: 'icon', label: '',
      render: (ph) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }} onClick={e => e.stopPropagation()}>
          <Avatar src={(ph as unknown as { avatarUrl?: string | null }).avatarUrl} name={`${ph.firstName} ${ph.lastName}`} size={28} />
        </div>
      ),
    },
    {
      key: 'name', label: 'Esterno',
      render: (ph) => (
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={{ fontWeight: 500, fontSize: 'var(--text-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {ph.firstName} {ph.lastName}
          </span>
          {ph.studioName && (
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <Building2 size={10} /> {ph.studioName}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'contact', label: 'Contatto',
      render: (ph) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <Mail size={11} /> {ph.email || '—'}
          </span>
          {ph.phone && (
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <Phone size={11} /> {ph.phone}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'city', label: 'Città',
      render: (ph) => (
        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {ph.city || '—'}
        </span>
      ),
    },
    {
      key: 'projects', label: 'Prog.', type: 'number',
      render: (ph) => (
        <span style={{
          background: 'rgba(255,255,255,0.06)', borderRadius: 'var(--radius-full)',
          padding: '0.125rem 0.5rem', fontSize: 'var(--text-xs)', fontWeight: 600,
          color: 'var(--color-text-muted)', textAlign: 'center',
          display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
        }}>
          <FolderOpen size={11} /> {ph._count?.projects ?? 0}
        </span>
      ),
    },
    {
      key: 'status', label: 'Stato',
      render: (ph) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'flex-start' }}>
          <Badge variant={ph.active ? 'success' : 'muted'}>{ph.active ? 'Attivo' : 'Inattivo'}</Badge>
          <ValidityBadge validUntil={(ph as unknown as { validUntil?: string | null }).validUntil} />
        </div>
      ),
    },
    {
      key: 'actions', label: '',
      render: (ph) => (
        <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
          <RowActionsMenu actions={[
            { label: 'Apri', onClick: () => openDrawer(ph) },
            { label: ph.active ? 'Disattiva' : 'Riattiva', icon: ph.active ? <UserX size={14} /> : <UserCheck size={14} />, onClick: () => toggleActiveMutation.mutate({ id: ph.id, active: !ph.active }) },
            { label: 'Elimina', icon: <Trash2 size={14} />, onClick: () => setDeleteTargetId(ph.id), variant: 'danger' },
          ]} />
        </div>
      ),
    },
  ];

  return (
    <div className="page-enter" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <PageHeader
        title="Esterni"
        subtitle={`${data?.meta.total ?? 0} esterni totali`}
        actions={[
          { label: 'Nuovo esterno', icon: <Plus size={16} />, onClick: () => { setForm(emptyForm()); setShowNewModal(true); }, variant: 'primary' },
        ]}
      />

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 320px', maxWidth: 420 }}>
          <Search size={15} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-faint)' }} />
          <input
            type="search"
            placeholder="Cerca per nome, studio, email, città…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{
              background: 'var(--glass-bg-card)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 'var(--radius-card)',
              color: 'var(--color-text)',
              fontSize: 'var(--text-sm)',
              padding: '0.5rem 0.75rem 0.5rem 2.25rem',
              width: '100%', outline: 'none',
            }}
          />
        </div>
        <button
          onClick={() => { setShowInactive(v => !v); setPage(1); }}
          style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--text-sm)', color: showInactive ? 'var(--color-text)' : 'var(--color-text-muted)' }}
        >
          {showInactive ? <ToggleRight size={18} style={{ color: 'var(--color-primary)' }} /> : <ToggleLeft size={18} />}
          Mostra inattivi
        </button>
      </div>

      <BulkActionBar
        count={sel.selectedCount}
        onClear={sel.clearSelection}
        actions={[
          { label: 'Disattiva', icon: <UserX size={13} />, variant: 'secondary', onClick: () => setBulkDeactivateOpen(true), loading: bulkDeactivateMutation.isPending },
          { label: 'Riattiva', icon: <UserCheck size={13} />, variant: 'secondary', onClick: () => setBulkReactivateOpen(true), loading: bulkReactivateMutation.isPending },
          { label: 'Elimina', icon: <Trash2 size={13} />, variant: 'danger', onClick: () => setBulkDeleteOpen(true), loading: bulkDeleteMutation.isPending },
        ]}
      />

      {isLoading ? (
        Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
      ) : (
      <DataTable
        columns={photoColumns}
        data={photographers}
        gridColumns="68px 2fr 1.5fr 1fr 0.5fr 0.6fr 44px"
        rowKey={(ph) => ph.id}
        onRowClick={(ph) => openDrawer(ph)}
        emptyState={
          <EmptyState
            icon={<Search size={32} />}
            title="Nessun esterno trovato"
            description={search ? 'Prova con un termine diverso' : 'Aggiungi il tuo primo esterno'}
            action={<Button onClick={() => setShowNewModal(true)} size="sm">Nuovo esterno</Button>}
          />
        }
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={(field) => handleSort(field as ExtPhotoSortField)}
        selectable
        selectedIds={sel.selectedIds}
        onSelectionChange={(ids) => sel.setSelectedIds(ids)}
        isLoading={isLoading}
        stickyHeader
      />
      )}
      {data && data.meta.pages > 1 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
          padding: '0.875rem',
        }}>
          <Button variant="ghost" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>←</Button>
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            Pagina {page} di {data.meta.pages}
          </span>
          <Button variant="ghost" size="sm" disabled={page >= data.meta.pages} onClick={() => setPage(p => p + 1)}>→</Button>
        </div>
      )}

      {/* Detail Drawer */}
      <Drawer
        open={!!selectedPhotographer}
        onClose={() => { setSelectedPhotographer(null); setEditMode(false); }}
        title={selectedPhotographer ? `${selectedPhotographer.firstName} ${selectedPhotographer.lastName}` : ''}
        width={500}
        footer={editMode ? (
          <>
            <Button variant="ghost" onClick={() => setEditMode(false)}>Annulla</Button>
            <Button onClick={handleUpdate} loading={updateMutation.isPending}>Salva</Button>
          </>
        ) : (
          <div style={{ display: 'flex', gap: '0.5rem', width: '100%', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => selectedPhotographer && toggleActiveMutation.mutate({ id: selectedPhotographer.id, active: !selectedPhotographer.active })}
                loading={toggleActiveMutation.isPending}
              >
                {selectedPhotographer?.active ? 'Disattiva' : 'Riattiva'}
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => selectedPhotographer && setDeleteTargetId(selectedPhotographer.id)}
              >
                Elimina
              </Button>
            </div>
            <Button
              onClick={() => {
                if (!selectedPhotographer) return;
                setForm({
                  firstName: selectedPhotographer.firstName,
                  lastName: selectedPhotographer.lastName,
                   email: selectedPhotographer.email ?? '',
                  studioName: selectedPhotographer.studioName ?? '',
                  phone: selectedPhotographer.phone ?? '',
                  city: selectedPhotographer.city ?? '',
                  address: selectedPhotographer.address ?? '',
                  vatNumber: selectedPhotographer.vatNumber ?? '',
                  fiscalCode: selectedPhotographer.fiscalCode ?? '',
                  notes: selectedPhotographer.notes ?? '',
                  active: selectedPhotographer.active,
                  validUntil: selectedPhotographer.validUntil?.slice(0, 10) ?? '',
                });
                setEditMode(true);
              }}
            >
              Modifica
            </Button>
          </div>
        )}
      >
        {selectedPhotographer && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {editMode ? (
              <form onSubmit={handleUpdate} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <Input label="Studio / Azienda" value={form.studioName} onChange={e => setForm(f => ({ ...f, studioName: e.target.value }))} />
                <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <Input label="Nome *" value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} required />
                  <Input label="Cognome *" value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} required />
                </div>
                <Input label="Email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                <Input label="Telefono" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <Input label="Città" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
                  <Input label="P. IVA" value={form.vatNumber} onChange={e => setForm(f => ({ ...f, vatNumber: e.target.value }))} />
                </div>
                <Input label="Indirizzo" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
                <Input label="Codice fiscale" value={form.fiscalCode} onChange={e => setForm(f => ({ ...f, fiscalCode: e.target.value }))} />
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--text-sm)', cursor: 'pointer', userSelect: 'none' }}>
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
                      style={{ accentColor: 'var(--color-primary)' }}
                    />
                    Esterno attivo
                  </label>
                </div>
                <div>
                  <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 600, display: 'block', marginBottom: '0.375rem' }}>Valido fino al</label>
                  <input
                    type="date"
                    value={form.validUntil}
                    onChange={e => setForm(f => ({ ...f, validUntil: e.target.value }))}
                    style={{
                      width: '100%', background: 'var(--glass-bg-card)',
                      border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)',
                      color: 'var(--color-text)', fontSize: 'var(--text-sm)',
                      padding: '0.5rem 0.75rem', outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <MarkdownEditor
                  label="Note"
                  value={form.notes}
                  onChange={v => setForm(f => ({ ...f, notes: v }))}
                  rows={5}
                />
              </form>
            ) : (
              <>
                <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <StatCard label="Totale fatturato" value={formatCurrency(totalBilled)} />
                  <StatCard label="Lavori attivi" value={String(activeJobs)} />
                </div>

                <InfoSection title="Anagrafica">
                  {selectedPhotographer.studioName && (
                    <InfoRow label="Studio" value={selectedPhotographer.studioName} />
                  )}
                  <InfoRow label="Email" value={selectedPhotographer.email} />
                  <InfoRow label="Telefono" value={selectedPhotographer.phone} />
                  <InfoRow label="Città" value={selectedPhotographer.city} />
                  <InfoRow label="Indirizzo" value={selectedPhotographer.address} />
                  <InfoRow label="P. IVA" value={selectedPhotographer.vatNumber} />
                  <InfoRow label="Codice fiscale" value={selectedPhotographer.fiscalCode} />
                  <InfoRow label="Iscritto dal" value={formatDate(selectedPhotographer.createdAt)} />
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', width: 120, flexShrink: 0 }}>Stato</span>
                    <Badge variant={selectedPhotographer.active ? 'success' : 'muted'}>
                      {selectedPhotographer.active ? 'Attivo' : 'Inattivo'}
                    </Badge>
                  </div>
                  {selectedPhotographer.validUntil && (
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', width: 120, flexShrink: 0 }}>Valido fino al</span>
                      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)', marginRight: '0.5rem' }}>{formatDate(selectedPhotographer.validUntil)}</span>
                      <ValidityBadge validUntil={selectedPhotographer.validUntil} />
                    </div>
                  )}
                </InfoSection>

                {selectedPhotographer.notes && (
                  <InfoSection title="Note">
                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
                      <ReactMarkdown>{selectedPhotographer.notes}</ReactMarkdown>
                    </div>
                  </InfoSection>
                )}

                {selectedPhotographer.projects && selectedPhotographer.projects.length > 0 && (
                  <InfoSection title={`Progetti (${selectedPhotographer.projects.length})`}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {selectedPhotographer.projects.map(pr => (
                        <button
                          key={pr.id}
                          onClick={() => { navigate(`/projects/${pr.id}`); setSelectedPhotographer(null); }}
                          style={{
                            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                            padding: '0.625rem 0.75rem',
                            background: 'var(--glass-bg-card)',
                            border: '1px solid transparent',
                            borderRadius: 'var(--radius-card)',
                            cursor: 'pointer',
                            transition: 'all var(--transition-interactive)',
                            width: '100%', textAlign: 'left', gap: '0.5rem',
                          }}
                          onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)')}
                          onMouseLeave={e => (e.currentTarget.style.borderColor = 'transparent')}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-primary)', fontWeight: 600 }}>{pr.code}</span>
                              <StatusBadgeSmall status={pr.status} />
                              {pr.externalPaymentStatus && <PaymentBadgeSmall status={pr.externalPaymentStatus} />}
                            </div>
                            <p style={{ fontSize: 'var(--text-sm)', fontWeight: 500, marginTop: '0.25rem', color: 'var(--color-text)' }}>{pr.title}</p>
                            <div style={{ display: 'flex', gap: '1rem', marginTop: '0.25rem' }}>
                              {pr.deadline && (
                                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                                  Scadenza: {formatDate(pr.deadline)}
                                </p>
                              )}
                              {pr.externalSalePrice != null && (
                                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                                  {formatCurrency(pr.externalSalePrice)}
                                </p>
                              )}
                            </div>
                          </div>
                          <ChevronRight size={14} style={{ color: 'var(--color-text-faint)', flexShrink: 0, marginTop: '0.25rem' }} />
                        </button>
                      ))}
                    </div>
                  </InfoSection>
                )}

                {selectedPhotographer.projects?.length === 0 && (
                  <div style={{
                    padding: '1.5rem', textAlign: 'center',
                    background: 'var(--glass-bg-card)',
                    borderRadius: 'var(--radius-card)',
                    border: '1px dashed rgba(255,255,255,0.10)',
                  }}>
                    <FolderOpen size={24} style={{ color: 'var(--color-text-faint)', margin: '0 auto 0.5rem' }} />
                    <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>Nessun progetto assegnato</p>
                  </div>
                )}

                {stats && (
                  <InfoSection title="Statistiche Prestazioni">
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <div style={{ background: 'var(--glass-bg-card)', borderRadius: 'var(--radius-card)', padding: '0.625rem', textAlign: 'center' }}>
                        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>Totale</p>
                        <p style={{ fontSize: 'var(--text-lg)', fontWeight: 700 }}>{stats.totalPerformances}</p>
                      </div>
                      <div style={{ background: 'var(--glass-bg-card)', borderRadius: 'var(--radius-card)', padding: '0.625rem', textAlign: 'center' }}>
                        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>Importo</p>
                        <p style={{ fontSize: 'var(--text-base)', fontWeight: 700 }}>{formatCurrency(stats.totalAgreedPrice)}</p>
                      </div>
                      <div style={{ background: 'rgba(16,185,129,0.08)', borderRadius: 'var(--radius-card)', padding: '0.625rem', textAlign: 'center' }}>
                        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-success)' }}>Incassato</p>
                        <p style={{ fontSize: 'var(--text-base)', fontWeight: 700, color: 'var(--color-success)' }}>{formatCurrency(stats.totalPayments)}</p>
                      </div>
                      <div style={{ background: 'var(--color-warning-highlight)', borderRadius: 'var(--radius-card)', padding: '0.625rem', textAlign: 'center' }}>
                        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-warning)' }}>Da incassare</p>
                        <p style={{ fontSize: 'var(--text-base)', fontWeight: 700, color: 'var(--color-warning)' }}>
                          {formatCurrency(stats.totalAgreedPrice - stats.totalPayments)}
                        </p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <Badge variant="default">{stats.byStatus.CONFERMATO ?? 0} Confermate</Badge>
                      <Badge variant="success">{stats.byStatus.COMPLETATO ?? 0} Completate</Badge>
                      <Badge variant="muted">{stats.byStatus.ANNULLATO ?? 0} Annullate</Badge>
                    </div>
                    {stats.byService.length > 0 && (
                      <div style={{ marginBottom: '0.75rem' }}>
                        <p style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '0.375rem' }}>Per servizio</p>
                        {stats.byService.map(s => (
                          <div key={s.serviceId} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-xs)', padding: '0.25rem 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                            <span>{s.serviceName}</span>
                            <span style={{ color: 'var(--color-text-muted)' }}>{s.count} &times; {formatCurrency(s.totalAgreed)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {stats.recentPerformances.length > 0 && (
                      <div>
                        <p style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: '0.375rem' }}>Recenti</p>
                        {stats.recentPerformances.map(rp => (
                          <div key={rp.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-xs)', padding: '0.25rem 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                            <span>{rp.title} &mdash; {formatDate(rp.eventDate)}</span>
                            <span style={{ color: 'var(--color-text-muted)' }}>{formatCurrency(rp.agreedPrice)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </InfoSection>
                )}
                {statsLoading && (
                  <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--color-text-faint)' }}>
                    Caricamento statistiche...
                  </div>
                )}

                <InfoSection title="Prezzi personalizzati">
                  <PriceOverrideSection extId={selectedPhotographer.id} />
                </InfoSection>
              </>
            )}
          </div>
        )}
      </Drawer>

      {/* New Photographer Modal */}
      <Modal
        open={showNewModal}
        onClose={() => setShowNewModal(false)}
        title="Nuovo esterno"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowNewModal(false)}>Annulla</Button>
            <Button onClick={handleCreate} loading={createMutation.isPending}>Crea esterno</Button>
          </>
        }
      >
        <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Input label="Studio / Azienda" value={form.studioName} onChange={e => setForm(f => ({ ...f, studioName: e.target.value }))} placeholder="es. Studio Luce & Ombra" />
          <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="Nome *" value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} required />
            <Input label="Cognome *" value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} required />
          </div>
          <Input label="Email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="Telefono" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
            <Input label="Città" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
          </div>
          <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="P. IVA" value={form.vatNumber} onChange={e => setForm(f => ({ ...f, vatNumber: e.target.value }))} />
            <Input label="Codice fiscale" value={form.fiscalCode} onChange={e => setForm(f => ({ ...f, fiscalCode: e.target.value }))} />
          </div>
          <div>
            <label style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 600, display: 'block', marginBottom: '0.375rem' }}>Valido fino al</label>
            <input
              type="date"
              value={form.validUntil}
              onChange={e => setForm(f => ({ ...f, validUntil: e.target.value }))}
              style={{
                width: '100%', background: 'var(--glass-bg-card)',
                border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)',
                color: 'var(--color-text)', fontSize: 'var(--text-sm)',
                padding: '0.5rem 0.75rem', outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <MarkdownEditor
            label="Note"
            value={form.notes}
            onChange={v => setForm(f => ({ ...f, notes: v }))}
            rows={3}
          />
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteTargetId}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={() => deleteTargetId && deleteMutation.mutate(deleteTargetId)}
        title="Elimina esterno"
        description="L'esterno verrà eliminato. I progetti associati rimarranno nel sistema."
        confirmLabel="Elimina"
        loading={deleteMutation.isPending}
      />

      <ConfirmDialog
        open={bulkDeactivateOpen}
        onClose={() => setBulkDeactivateOpen(false)}
        onConfirm={() => bulkDeactivateMutation.mutate(selectedIds)}
        title="Disattiva esterni"
        description={`Disattivare ${sel.selectedCount} esterni selezionati?`}
        confirmLabel="Disattiva"
        loading={bulkDeactivateMutation.isPending}
      />

      <ConfirmDialog
        open={bulkReactivateOpen}
        onClose={() => setBulkReactivateOpen(false)}
        onConfirm={() => bulkReactivateMutation.mutate(selectedIds)}
        title="Riattiva esterni"
        description={`Riattivare ${sel.selectedCount} esterni selezionati?`}
        confirmLabel="Riattiva"
        loading={bulkReactivateMutation.isPending}
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={() => bulkDeleteMutation.mutate(selectedIds)}
        title="Elimina esterni"
        description={`Eliminare ${sel.selectedCount} esterni selezionati? Questa operazione è irreversibile.`}
        confirmLabel="Elimina"
        requiresConfirmCheck
        loading={bulkDeleteMutation.isPending}
      />
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div style={{
      background: 'var(--glass-bg-card)',
      border: '1px solid rgba(255,255,255,0.10)',
      borderRadius: 'var(--radius-card)',
      padding: '0.875rem 1rem',
    }}>
      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>{label}</p>
      <p style={{ fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--color-text)' }}>{value}</p>
    </div>
  );
}
